import type {
  ResolvedRequest,
  ResponseType,
  Transport,
  TransportResult,
} from '@/shared/api/generated/.kubb/client'

const detectResponseType = (contentType: string | null): ResponseType | undefined => {
  if (!contentType) {
    return undefined
  }
  if (contentType.includes('text/event-stream')) {
    return 'stream'
  }
  if (contentType.includes('application/json') || contentType.includes('text/json')) {
    return 'json'
  }
  if (contentType.includes('text/')) {
    return 'text'
  }
  if (
    contentType.includes('image/') ||
    contentType.includes('application/octet-stream') ||
    contentType.includes('application/pdf')
  ) {
    return 'blob'
  }
  return undefined
}

const parseResponse = async (response: Response, responseType?: ResponseType) => {
  if (
    response.status === 204 ||
    response.status === 205 ||
    response.status === 304 ||
    !response.body
  ) {
    return undefined
  }

  switch (responseType ?? detectResponseType(response.headers.get('Content-Type'))) {
    case 'text':
    case 'document':
      return response.text()
    case 'blob':
      return response.blob()
    case 'arraybuffer':
      return response.arrayBuffer()
    case 'stream':
      return response.body
    case 'json': {
      const body = await response.text()
      return body ? JSON.parse(body) : undefined
    }
  }

  const text = await response.text()
  if (!text) {
    return undefined
  }
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

const getContentType = (headers: Headers) =>
  headers.get('Content-Type')?.split(';')[0]?.trim().toLowerCase() || undefined

export const fetchTransport: Transport = async (
  request: ResolvedRequest
): Promise<TransportResult> => {
  const init: RequestInit = {
    ...request.options,
    method: request.method,
    headers: request.headers,
    body: request.body,
    signal: request.signal,
    credentials: request.credentials ?? 'include',
  }

  const nativeRequest = new Request(request.url, init)
  const response = await globalThis.fetch(nativeRequest)

  return {
    data: await parseResponse(response, request.responseType),
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    contentType: getContentType(response.headers),
    request: nativeRequest,
    response,
  }
}

type AuthenticatedTransportOptions = {
  transport: Transport
  refreshAccessToken: () => Promise<unknown>
  getAccessToken: () => string | undefined
}

export const createAuthenticatedTransport = ({
  transport,
  refreshAccessToken,
  getAccessToken,
}: AuthenticatedTransportOptions): Transport =>
  async function authenticatedTransport(request) {
    const result = await transport(request)
    const authorizationHeader = Object.keys(request.headers).find(
      (header) => header.toLowerCase() === 'authorization'
    )

    if (result.status !== 401 || !authorizationHeader) {
      return result
    }

    await refreshAccessToken()
    const accessToken = getAccessToken()
    if (!accessToken) {
      return result
    }

    return transport({
      ...request,
      headers: {
        ...request.headers,
        [authorizationHeader]: `Bearer ${accessToken}`,
      },
    })
  }
